import { StatusBar, Style } from '@capacitor/status-bar';
import { isAndroidNativeOnly, isNativeNotWeb } from '@/utils/platform-check';
import { BF_PLEDGE_BANNER_FIRST_TOP } from '@/constants/MyGiving/building-fund-pledge';
import { AGM_STATUS_BAR_COLOR } from '@/constants/agm';

/**
 * Screens whose colour runs up to the top edge claim the status bar, so its icons
 * stay legible and, on Android, its background matches.
 *
 * Two cases on Android, from `styles.xml` opting out of edge-to-edge:
 *  - Android 15 and below honour the opt-out: the WebView starts BELOW the status
 *    bar, `env(safe-area-inset-top)` is 0, and the bar is native — painting it is
 *    the only way the screen's colour reaches the top.
 *  - Android 16+ ignore it (targetSdk 36), so the WebView draws under the bar and
 *    the page pads the inset itself. `setBackgroundColor` is a no-op there.
 * iOS is always the second case; only the icon style matters.
 *
 * Owners claim and release by key. The newest claim wins, and releasing the last
 * one puts back the bar as it was before the first. Ionic fires the entering
 * page's hooks before the leaving page's, so a release can arrive after the next
 * screen's claim; keying claims makes that order irrelevant.
 */

const claims = new Map();
let original = null;
let scheduled = null;
let running = Promise.resolve();

const readToken = name =>
  getComputedStyle(document.documentElement).getPropertyValue(name).trim() || null;

// Light icons on the BF2026 navy (`campaign-primary`, owned by the design system).
export const campaignStatusBar = () => ({
  style: Style.Dark,
  color: readToken('--hogc-campaign-primary'),
});

// Dark icons over a first-timer's light "My First" banner (D1b–D4b).
export const campaignFirstStatusBar = () => ({
  style: Style.Light,
  color: BF_PLEDGE_BANNER_FIRST_TOP,
});

// Light icons on the AGM page's dark brown event header.
export const agmStatusBar = () => ({ style: Style.Dark, color: AGM_STATUS_BAR_COLOR });

const paint = async ({ style, color }) => {
  if (style) await StatusBar.setStyle({ style });
  if (color && isAndroidNativeOnly()) await StatusBar.setBackgroundColor({ color });
};

async function apply() {
  const claim = [...claims.values()].pop();
  if (claim) {
    if (!original) original = await StatusBar.getInfo();
    await paint(claim);
  } else if (original) {
    const { style, color } = original;
    original = null;
    await paint({ style, color });
  }
}

// One native round per burst of claims and releases (a navigation makes both),
// run one at a time so a round never reads the bar another is half-way through.
function schedule() {
  if (!isNativeNotWeb()) return Promise.resolve();
  if (scheduled) return scheduled;
  scheduled = running = running
    .then(() => {
      scheduled = null;
      return apply();
    })
    .catch(error => console.warn('[StatusBar] Could not update the status bar:', error));
  return scheduled;
}

export function claimStatusBar(owner, theme) {
  claims.delete(owner);
  claims.set(owner, theme);
  return schedule();
}

export function releaseStatusBar(owner) {
  return claims.delete(owner) ? schedule() : Promise.resolve();
}

export const hasStatusBarClaim = () => claims.size > 0;
